import type { UserMemory } from '../../shared/memory.ts';

/**
 * Reads memory until the call counts as completed: the server answered /complete before a slow updater did, and its
 * job finishes the call on its own. Gives up `giveUpMs` after it began, by the clock (a read can hang), with the last
 * read: an updater that failed leaves the call for the next start's catch-up, and memory can't tell that apart from
 * one still running. Stops at once when `stopped()` says nobody is waiting.
 */
export async function untilCompleted(
  read: () => Promise<UserMemory>,
  sessionId: string,
  opts: {
    everyMs: number;
    giveUpMs: number;
    stopped: () => boolean;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
    /** What a hanging read is raced against: resolves after `ms`. */
    timeout?: (ms: number) => Promise<void>;
  },
): Promise<UserMemory | null> {
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = opts.now ?? Date.now;
  const timeout = opts.timeout ?? sleep;
  const until = now() + opts.giveUpMs;
  let last: UserMemory | null = null;
  while (now() < until && !opts.stopped()) {
    await sleep(opts.everyMs);
    if (opts.stopped()) break;
    // A failed read is one more wait, not the end: the server is the one finishing the call. A read that hangs counts
    // against the budget too: it's raced against what's left of it.
    const left = until - now();
    if (left <= 0) break;
    last = await Promise.race([read().catch(() => last), timeout(left).then(() => last)]);
    if (last?.completedSessions.includes(sessionId)) return last;
  }
  return last;
}
